/* =========================
   PEGUU — STICKER SUMMARY
   AGREGAR PEDIDO AL CARRITO
========================= */

(() => {
  "use strict";


  /* =========================
     CONFIGURACIÓN
  ========================= */

  const API_BASE_URL =
    window.PeguuApi.endpoint("customers");

  const AUTH_STORAGE_KEY =
    "peguu:customer-auth";

  const GUEST_CART_STORAGE_KEY =
    "peguu:guest-cart";

  const PAYMENT_STORAGE_KEY =
    "peguu:sticker-payment";

  const CART_EDIT_STORAGE_KEY =
    "peguu:cart-edit";


  /* =========================
     POSICIÓN INICIAL DEL PASO 4
  ========================= */

  const resetSummaryScrollPosition = () => {
    window.scrollTo(0, 0);
  };

  if (
    "scrollRestoration" in
    window.history
  ) {
    window.history.scrollRestoration =
      "manual";
  }

  resetSummaryScrollPosition();

  window.addEventListener(
    "pageshow",
    () => {
      const hasAuthenticatedPaymentIntent =
        new URLSearchParams(
          window.location.search
        ).has("open-payment") &&
        Boolean(
          readCustomerSession()?.token
        );

      if (hasAuthenticatedPaymentIntent) {
        return;
      }

      resetSummaryScrollPosition();

      window.requestAnimationFrame(
        resetSummaryScrollPosition
      );
    }
  );


  /* =========================
     CLAVES DEL PEDIDO ACTUAL
  ========================= */

  const MATERIAL_STORAGE_KEY =
    "peguu:selected-material";

  const ORDER_STORAGE_KEY =
    "peguu:sticker-order";

  const DESIGN_STORAGE_KEY =
    "peguu:sticker-design";

  const DESIGN_FILE_DB =
    "peguu-design-files";

  const DESIGN_FILE_STORE =
    "files";

  const QR_STORAGE_KEYS = [
    "peguu:qr-config",
    "peguu:qr-url",
    "peguu:qr-position",
    "peguu:qr-finish",
  ];


  /* =========================
     LEER JSON
  ========================= */

  const parseStorageJson = (
    storage,
    key
  ) => {
    const storedValue =
      storage.getItem(key);

    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue);
    } catch (error) {
      console.warn(
        `Peguu Summary: no se pudo leer ${key}.`,
        error
      );

      return null;
    }
  };


  const parseSessionJson = (key) => {
    return parseStorageJson(
      window.sessionStorage,
      key
    );
  };


  const parseLocalJson = (key) => {
    return parseStorageJson(
      window.localStorage,
      key
    );
  };

  const writeSessionJson = (
    key,
    value
  ) => {
    try {
      window.sessionStorage.setItem(
        key,
        JSON.stringify(value)
      );
    } catch (error) {
      console.warn(
        `Peguu Summary: no se pudo guardar ${key}.`,
        error
      );
    }
  };


  /* =========================
     UTILIDADES
  ========================= */

  const firstDefined = (...values) => {
    return values.find((value) => {
      return (
        value !== undefined &&
        value !== null &&
        value !== ""
      );
    });
  };


  const normalizeText = (value) => {
    return String(value || "").trim();
  };


  const normalizeNumber = (value) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return null;
    }

    if (typeof value === "number") {
      return Number.isFinite(value)
        ? value
        : null;
    }

    const normalizedValue = String(value)
      .trim()
      .replace(/\s/g, "")
      .replace(/,/g, "")
      .replace(/[^\d.-]/g, "");

    const number =
      Number(normalizedValue);

    return Number.isFinite(number)
      ? number
      : null;
  };

  const createCartSignature = (cart) => {
    const items = Array.isArray(cart?.items)
      ? cart.items
      : [];

    if (!items.length) {
      return "";
    }

    const serializedItems = items
      .map((item) => JSON.stringify({
        id: firstDefined(
          item?.id,
          item?.itemId,
          item?.cartItemId,
          item?.localItemId,
          ""
        ),
        materialId: item?.materialId || "",
        productCode: item?.productCode || "",
        quantity: item?.quantity || 0,
        widthCm: item?.widthCm || 0,
        heightCm: item?.heightCm || 0,
        fileId: item?.fileId || item?.design?.fileId || "",
        fileId2: item?.fileId2 || item?.design2?.fileId || ""
      }))
      .sort()
      .join("|");

    let hash = 2166136261;

    for (let index = 0; index < serializedItems.length; index += 1) {
      hash ^= serializedItems.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }

    return `${items.length}:${(hash >>> 0).toString(36)}`;
  };


  const getPageNavigationType = () => {
    const navigationEntry =
      window.performance
        ?.getEntriesByType?.(
          "navigation"
        )?.[0];

    if (navigationEntry?.type) {
      return navigationEntry.type;
    }

    const legacyNavigationType =
      window.performance
        ?.navigation?.type;

    if (legacyNavigationType === 1) {
      return "reload";
    }

    if (legacyNavigationType === 2) {
      return "back_forward";
    }

    return "navigate";
  };


  const createLocalItemId = () => {
    if (
      window.crypto &&
      typeof window.crypto.randomUUID ===
        "function"
    ) {
      return window.crypto.randomUUID();
    }

    return (
      `guest-${Date.now()}-` +
      Math.random()
        .toString(36)
        .slice(2, 10)
    );
  };


  /* =========================
     SESIÓN DEL CLIENTE
  ========================= */

  const readCustomerSession = () => {
    /*
     * Preferimos la API pública
     * del modal de autenticación.
     */

    if (
      window.PeguuAuth &&
      typeof window.PeguuAuth.getSession ===
        "function"
    ) {
      return (
        window.PeguuAuth.getSession() ||
        null
      );
    }

    /*
     * Respaldo en caso de que el archivo
     * de autenticación aún no haya cargado.
     */

    return parseLocalJson(
      AUTH_STORAGE_KEY
    );
  };


  const getCustomerToken = () => {
    const session =
      readCustomerSession();

    return normalizeText(
      session?.token
    );
  };


  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      .test(normalizeText(value).toLowerCase());
  };


  const getCustomerFirstName = (
    customer
  ) => {
    const email = normalizeText(
      customer?.email
    ).toLowerCase();

    const candidates = [
      customer?.firstName,
      customer?.first_name,
      customer?.fullName,
      customer?.name,
    ];

    const usableName = candidates.find(
      (value) => {
        const name = normalizeText(value);

        return Boolean(
          name &&
          name.toLowerCase() !== "cliente" &&
          name.toLowerCase() !== email &&
          !isValidEmail(name)
        );
      }
    );

    return normalizeText(usableName)
      .split(/\s+/)
      .filter(Boolean)[0] || "";
  };


  const syncSummaryGreeting = (
    section
  ) => {
    const greeting = section.querySelector(
      "[data-peguu-summary-welcome]"
    );

    const greetingName = greeting?.querySelector(
      "[data-peguu-summary-welcome-name]"
    );

    if (!greeting || !greetingName) {
      return;
    }

    const session = readCustomerSession();
    const firstName = session?.token
      ? getCustomerFirstName(
          session.customer
        )
      : "";

    greetingName.textContent = firstName;
    greeting.hidden = !firstName;
  };


  const getLocalDesignFile = (key) => {
    const previewKey = normalizeText(key);

    if (!previewKey || !window.indexedDB) {
      return Promise.resolve(null);
    }

    return new Promise((resolve) => {
      const request = indexedDB.open(
        DESIGN_FILE_DB,
        1
      );

      request.onupgradeneeded = () => {
        if (
          !request.result.objectStoreNames
            .contains(DESIGN_FILE_STORE)
        ) {
          request.result.createObjectStore(
            DESIGN_FILE_STORE
          );
        }
      };

      request.onerror = () => resolve(null);
      request.onsuccess = () => {
        const database = request.result;
        const transaction = database.transaction(
          DESIGN_FILE_STORE,
          "readonly"
        );
        const getRequest = transaction
          .objectStore(DESIGN_FILE_STORE)
          .get(previewKey);

        getRequest.onsuccess = () => {
          resolve(getRequest.result || null);
          database.close();
        };
        getRequest.onerror = () => {
          resolve(null);
          database.close();
        };
      };
    });
  };


  const uploadCartDesignFile = async (
    file
  ) => {
    if (!file) {
      return null;
    }

    const token = getCustomerToken();
    const headers = {
      Accept: "application/json"
    };

    if (token) {
      headers.Authorization =
        `Bearer ${token}`;
    }

    const formData = new FormData();
    formData.append("file", file);

    const response = await window.PeguuAuthClient.authFetch(
      `${API_BASE_URL}/me/cart/design`,
      {
        method: "POST",
        headers: {
          Accept: "application/json"
        },
        body: formData
      }
    );

    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (
      response.status !== 201 ||
      payload?.success === false
    ) {
      throw new Error(
        payload?.message ||
        "No pudimos cargar el archivo. Vuelve al paso de diseño e inténtalo de nuevo."
      );
    }

    const uploadedFile =
      payload?.data?.file ||
      payload?.data ||
      payload;

    const fileId = normalizeText(
      firstDefined(
        uploadedFile?.fileId,
        uploadedFile?.id
      )
    );

    if (!fileId) {
      throw new Error(
        "La respuesta de carga no incluyó el identificador del archivo."
      );
    }

    return {
      fileId,
      fileUrl: normalizeText(
        firstDefined(
          uploadedFile?.url,
          uploadedFile?.fileUrl
        )
      ) || null
    };
  };


  const ensureCartItemDesignFile = async (
    item
  ) => {
    if (!item || item.design?.uploadLater === true) {
      return item;
    }

    if (normalizeText(item.fileId) || normalizeText(item.design?.fileId)) {
      return {
        ...item,
        fileId:
          normalizeText(item.fileId) ||
          normalizeText(item.design?.fileId)
      };
    }

    const hasSelectedFile = Boolean(
      item.design?.fileName ||
      item.design?.localPreviewKey
    );

    if (!hasSelectedFile) {
      return item;
    }

    const file = await getLocalDesignFile(
      item.design?.localPreviewKey
    );

    if (!file) {
      throw new Error(
        "No encontramos el archivo de diseño. Vuelve al paso de diseño y súbelo de nuevo."
      );
    }

    const uploaded = await uploadCartDesignFile(file);

    return {
      ...item,
      fileId: uploaded.fileId,
      design: {
        ...(item.design || {}),
        fileId: uploaded.fileId,
        fileUrl: uploaded.fileUrl,
        uploadStatus: "uploaded"
      }
    };
  };


  const isCustomerAuthenticated = () => {
    return Boolean(
      getCustomerToken()
    );
  };


  /* =========================
     DATOS DEL MATERIAL
  ========================= */

  const getMaterialId = (
    material,
    order
  ) => {
    return normalizeText(
      firstDefined(
        material?.id,
        material?.materialId,
        order?.material?.id,
        order?.materialId
      )
    );
  };


  const getMaterialName = (
    material,
    order
  ) => {
    return normalizeText(
      firstDefined(
        material?.name,
        material?.materialName,
        material?.title,
        order?.material?.name,
        order?.materialName,
        order?.productName,
        "Stickers personalizados"
      )
    );
  };


  const getMaterialImageUrl = (
    material,
    order
  ) => {
    return normalizeText(
      firstDefined(
        material?.imageUrl,
        material?.image,
        material?.thumbnailUrl,
        order?.material?.imageUrl,
        order?.materialImageUrl
      )
    );
  };


  const getProductCode = (
    material,
    order
  ) => {
    return normalizeText(
      firstDefined(
        material?.productCode,
        material?.product_code,
        order?.material?.productCode,
        order?.productCode
      )
    );
  };


  /* =========================
     MEDIDAS
  ========================= */

  const getWidthCm = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.measurements?.widthCm,
        order?.measurements?.width,
        order?.widthCm,
        order?.width,
        order?.ancho,
        order?.dimensions?.width,
        order?.size?.width
      )
    );
  };


  const getHeightCm = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.measurements?.heightCm,
        order?.measurements?.height,
        order?.heightCm,
        order?.height,
        order?.alto,
        order?.dimensions?.height,
        order?.size?.height
      )
    );
  };


  /* =========================
     COTIZACIÓN
  ========================= */

  const getQuantity = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.quote?.quantity,
        order?.quantity,
        order?.pieces,
        order?.pieceQuantity,
        order?.selectedQuantity,
        order?.cantidad,
        order?.cantidadPiezas
      )
    );
  };


  const getUnitPrice = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.quote?.unitPrice,
        order?.unitPrice,
        order?.pricePerUnit
      )
    );
  };


  const getSubtotal = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.quote?.subtotal,
        order?.subtotal,
        order?.total
      )
    );
  };


  const getShippingCost = (order) => {
    return normalizeNumber(
      firstDefined(
        order?.quote?.shippingCost,
        order?.shippingCost,
        0
      )
    );
  };


  /* =========================
     DATOS ADICIONALES
  ========================= */

  const getShape = (order) => {
    return normalizeText(
      firstDefined(
        order?.shape,
        order?.cut,
        order?.cutType,
        order?.selectedShape,
        order?.forma,
        order?.tipoCorte
      )
    );
  };


  const getQrConfiguration = () => {
    const qrConfig =
      parseSessionJson(
        "peguu:qr-config"
      ) || {};

    const destinationUrl =
      normalizeText(
        qrConfig?.destinationUrl ||
        qrConfig?.link ||
        ""
      );

    return {
      destinationUrl,

      acabado:
        normalizeText(
          qrConfig?.acabado ||
          window.sessionStorage.getItem(
            "peguu:qr-finish"
          ) ||
          ""
        ),

      posicion:
        normalizeText(
          qrConfig?.posicion ||
          window.sessionStorage.getItem(
            "peguu:qr-position"
          ) ||
          ""
        ),
    };
  };


  /* =========================
     NORMALIZAR DISEÑO
  ========================= */

  const normalizeDesign = (
    design,
    order
  ) => {
    const storedDesign =
      design?.primaryFile
        ? {
            ...design.primaryFile,
            uploadLater:
              design.uploadLater === true
          }
        : design;

    const source =
      storedDesign ||
      order?.design ||
      (
        design?.uploadLater === true
          ? { uploadLater: true }
          : null
      );

    if (!source) {
      return null;
    }

    const fileId =
      normalizeText(
        firstDefined(
          source?.fileId,
          source?.file_id
        )
      ) || null;

    return {
      fileId,

      uploadLater:
        source?.uploadLater === true ||
        source?.upload_later === true,

      fileName:
        normalizeText(
          firstDefined(
            source?.fileName,
            source?.originalName
          )
        ) || null,

      fileType:
        normalizeText(
          firstDefined(
            source?.fileType,
            source?.mimeType
          )
        ) || null,

      fileSize:
        normalizeNumber(
          firstDefined(
            source?.fileSize,
            source?.size
          )
        ),

      previewUrl:
        normalizeText(
          firstDefined(
            source?.previewUrl,
            source?.fileUrl,
            source?.url
          )
        ) || null,

      localPreviewKey:
        normalizeText(
          source?.localPreviewKey
        ) || null,
    };
  };


  /* =========================
     CONSTRUIR ARTÍCULO
  ========================= */

  const buildCartItem = ({
    material,
    order,
    design,
  }) => {
    const materialId =
      getMaterialId(
        material,
        order
      );

    const widthCm =
      getWidthCm(order);

    const heightCm =
      getHeightCm(order);

    const quantity =
      getQuantity(order);

    const normalizedDesign =
      normalizeDesign(
        design,
        order
      );

    const productCode =
      getProductCode(
        material,
        order
      );

    const isQrProduct =
      productCode
        .toLowerCase() ===
      "qr_sticker";

    return {
      materialId,

      materialName:
        getMaterialName(
          material,
          order
        ),

      materialImageUrl:
        getMaterialImageUrl(
          material,
          order
        ),

      productCode,

      widthCm,
      heightCm,
      quantity,

      unitPrice:
        getUnitPrice(order),

      subtotal:
        getSubtotal(order),

      shippingCost:
        getShippingCost(order),

      shippingPending:
        order?.quote
          ?.shippingPending === true,

      shape:
        getShape(order),

      fileId:
        normalizedDesign?.fileId ||
        null,

      fileId2:
        null,

      design:
        normalizedDesign,

      qrConfig:
        isQrProduct
          ? getQrConfiguration()
          : null,
    };
  };


  /* =========================
     VALIDAR ARTÍCULO
  ========================= */

  const validateCartItem = (item) => {
    if (!item.materialId) {
      throw new Error(
        "No encontramos el identificador del material."
      );
    }

    if (
      !Number.isFinite(item.widthCm) ||
      item.widthCm <= 0
    ) {
      throw new Error(
        "El ancho del pedido no es válido."
      );
    }

    if (
      !Number.isFinite(item.heightCm) ||
      item.heightCm <= 0
    ) {
      throw new Error(
        "El alto del pedido no es válido."
      );
    }

    if (
      !Number.isInteger(item.quantity) ||
      item.quantity <= 0
    ) {
      throw new Error(
        "La cantidad del pedido no es válida."
      );
    }
  };


  const createCartItemFingerprint = (
    item
  ) => {
    return JSON.stringify({
      materialId:
        item.materialId,

      widthCm:
        item.widthCm,

      heightCm:
        item.heightCm,

      quantity:
        item.quantity,

      fileId:
        item.fileId || null,

      qrConfig:
        item.qrConfig || null,
    });
  };


  const getStoredCartAddition = (
    order,
    fingerprint
  ) => {
    const cartAddition =
      order?.cartAddition;

    if (
      cartAddition?.added !== true ||
      cartAddition?.fingerprint !==
        fingerprint ||
      !cartAddition?.itemId
    ) {
      return null;
    }

    return {
      source:
        cartAddition.source ===
        "customer"
          ? "customer"
          : "guest",

      item: {
        id:
          cartAddition.itemId,

        localItemId:
          cartAddition.itemId,
      },
    };
  };


  const saveCartAddition = (
    result,
    fingerprint
  ) => {
    const itemId =
      result?.item?.id ||
      result?.item?.localItemId ||
      "";

    if (!itemId) {
      return;
    }

    window.PeguuOrder?.update?.({
      cartAddition: {
        added: true,
        fingerprint,
        itemId,
        source:
          result.source,
      },
    });
  };


  /* =========================
     CARRITO DE INVITADO
  ========================= */

  const readGuestCart = () => {
    const storedCart =
      parseLocalJson(
        GUEST_CART_STORAGE_KEY
      );

    return Array.isArray(storedCart)
      ? storedCart
      : [];
  };


  const saveGuestCart = (items) => {
    window.localStorage.setItem(
      GUEST_CART_STORAGE_KEY,
      JSON.stringify(items)
    );
  };


  const addGuestCartItem = (item) => {
    const cart =
      readGuestCart();

    const guestItem = {
      ...item,

      localItemId:
        createLocalItemId(),

      source:
        "guest",

      createdAt:
        new Date().toISOString(),

      updatedAt:
        new Date().toISOString(),
    };

    cart.push(guestItem);

    saveGuestCart(cart);

    return guestItem;
  };


  /* =========================
     CARRITO AUTENTICADO
  ========================= */

  const parseApiResponse = async (
    response
  ) => {
    let payload = null;

    try {
      payload =
        await response.json();
    } catch (error) {
      payload = null;
    }

    if (
      !response.ok ||
      !payload?.success
    ) {
      const apiError =
        new Error(
          payload?.message ||
          "No fue posible agregar el producto al carrito."
        );

      apiError.status =
        response.status;

      apiError.code =
        payload?.code ||
        null;

      throw apiError;
    }

    return payload;
  };


  const addAuthenticatedCartItem = async (
    item,
    token
  ) => {
    const response = await window.PeguuAuthClient.authFetch(
      `${API_BASE_URL}/me/cart/items`,
      {
        method: "POST",

        headers: {
          "Content-Type":
            "application/json",

          Accept:
            "application/json",
        },

        body: JSON.stringify({
          materialId:
            item.materialId,

          widthCm:
            item.widthCm,

          heightCm:
            item.heightCm,

          quantity:
            item.quantity,

          fileId:
            item.fileId || null,

          qrConfig:
            item.qrConfig || null,
        }),
      }
    );

    const payload =
      await parseApiResponse(
        response
      );

    /*
     * La API actualmente devuelve solamente
     * el artículo persistido. Guardamos una
     * referencia visual local para que el drawer
     * pueda mostrar nombre, imagen y precio.
     */

    const apiItem =
      payload?.data?.item ||
      payload?.data?.cartItem ||
      payload?.data;

    const apiItemId =
      normalizeText(
        firstDefined(
          apiItem?.id,
          apiItem?._id,
          apiItem?.itemId,
          apiItem?.cartItemId
        )
      );

    if (!apiItemId) {
      throw new Error(
        "La API no devolvió el identificador del producto agregado."
      );
    }

    saveAuthenticatedItemMetadata(
      apiItemId,
      item
    );

    return {
      ...apiItem,
      id: apiItemId
    };
  };


  /* =========================
     METADATOS VISUALES API
  ========================= */

  const CUSTOMER_CART_METADATA_KEY =
    "peguu:customer-cart-metadata";


  const readAuthenticatedMetadata = () => {
    const storedMetadata =
      parseLocalJson(
        CUSTOMER_CART_METADATA_KEY
      );

    return (
      storedMetadata &&
      typeof storedMetadata === "object" &&
      !Array.isArray(storedMetadata)
    )
      ? storedMetadata
      : {};
  };


  const saveAuthenticatedItemMetadata = (
    itemId,
    item
  ) => {
    if (!itemId) {
      return;
    }

    const metadata =
      readAuthenticatedMetadata();

    metadata[itemId] = {
      materialName:
        item.materialName,

      materialImageUrl:
        item.materialImageUrl,

      productCode:
        item.productCode,

      shape:
        item.shape,

      design:
        item.design,

      qrConfig:
        item.qrConfig,

      updatedAt:
        new Date().toISOString(),
    };

    window.localStorage.setItem(
      CUSTOMER_CART_METADATA_KEY,
      JSON.stringify(metadata)
    );
  };


  /* =========================
     AGREGAR SEGÚN SESIÓN
  ========================= */

  const addCurrentOrderToCart = async (
    item
  ) => {
    const itemWithDesign =
      await ensureCartItemDesignFile(
        item
      );

    const token =
      getCustomerToken();

    if (token) {
      const apiItem =
        await addAuthenticatedCartItem(
          itemWithDesign,
          token
        );

      return {
        source:
          "customer",

        item:
          apiItem,
      };
    }

    const guestItem =
      addGuestCartItem(itemWithDesign);

    return {
      source:
        "guest",

      item:
        guestItem,
    };
  };


  /* =========================
     LIMPIAR PEDIDO ACTUAL
  ========================= */

  const clearCurrentOrder = () => {
    window.sessionStorage.removeItem(
      MATERIAL_STORAGE_KEY
    );

    window.sessionStorage.removeItem(
      ORDER_STORAGE_KEY
    );

    window.sessionStorage.removeItem(
      DESIGN_STORAGE_KEY
    );

    QR_STORAGE_KEYS.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
  };


  /* =========================
     ESTADO DEL BOTÓN
  ========================= */

  const setButtonLoading = (
    button,
    isLoading
  ) => {
    if (!button) {
      return;
    }

    const label =
      button.querySelector(
        ".peguu-button__text"
      );

    if (isLoading) {
      if (label) {
        label.dataset.originalText =
          label.textContent.trim();

        label.textContent =
          "AGREGANDO...";
      }

      button.disabled = true;

      button.classList.add(
        "is-loading"
      );

      button.setAttribute(
        "aria-busy",
        "true"
      );

      return;
    }

    button.disabled = false;

    button.classList.remove(
      "is-loading"
    );

    button.removeAttribute(
      "aria-busy"
    );

    if (
      label &&
      label.dataset.originalText
    ) {
      label.textContent =
        label.dataset.originalText;

      delete label.dataset.originalText;
    }
  };


  /* =========================
     MENSAJES
  ========================= */

  const removeExistingMessage = (
    section
  ) => {
    section
      .querySelector(
        "[data-peguu-summary-message]"
      )
      ?.remove();
  };


  const showMessage = (
    section,
    message,
    type = "error"
  ) => {
    removeExistingMessage(section);

    const messageElement =
      document.createElement("p");

    messageElement.className =
      `peguu-sticker-summary__message ` +
      `peguu-sticker-summary__message--${type}`;

    messageElement.dataset
      .peguuSummaryMessage =
      "";

    if (type === "error") {
      messageElement.setAttribute(
        "role",
        "alert"
      );
    } else {
      messageElement.setAttribute(
        "aria-live",
        "polite"
      );
    }

    messageElement.textContent =
      message;

    const actions =
      section.querySelector(
        ".peguu-sticker-summary__actions"
      );

    /*
    * El mensaje se coloca antes del
    * contenedor de acciones y no dentro.
    * Así no mueve ni reduce los botones.
    */

    actions?.before(
      messageElement
    );
  };


  /* =========================
     NOTIFICAR CAMBIO
  ========================= */

  const notifyCartUpdated = (
    detail
  ) => {
    document.dispatchEvent(
      new CustomEvent(
        "peguu:cart:updated",
        {
          detail,
        }
      )
    );

  };


  const refreshCart = async () => {
    if (
      window.PeguuCart &&
      typeof window.PeguuCart.refresh ===
        "function"
    ) {
      return window.PeguuCart.refresh();
    }

    document.dispatchEvent(
      new CustomEvent(
        "peguu:cart:refresh"
      )
    );

    return null;
  };


  const waitForStickerPayment = (
    timeoutMs = 5000
  ) => {
    const startedAt = Date.now();

    return new Promise(
      (resolve, reject) => {
        const checkPaymentApi = () => {
          const paymentApi =
            window.PeguuStickerPayment;

          if (
            typeof paymentApi?.init ===
              "function"
          ) {
            resolve(paymentApi);
            return;
          }

          if (
            Date.now() - startedAt >=
              timeoutMs
          ) {
            reject(
              new Error(
                "No fue posible cargar el bloque de pago."
              )
            );
            return;
          }

          window.setTimeout(
            checkPaymentApi,
            50
          );
        };

        checkPaymentApi();
      }
    );
  };


  /* =========================
     INICIALIZACIÓN
  ========================= */

  const initializeSummary = (
    section
  ) => {
    if (
      section.dataset
        .peguuSummaryInitialized ===
      "true"
    ) {
      return;
    }

    section.dataset
      .peguuSummaryInitialized =
      "true";

    syncSummaryGreeting(section);

    const pageNavigationType =
      getPageNavigationType();

    const isDirectPaymentEntry =
      pageNavigationType === "navigate" &&
      new URLSearchParams(
        window.location.search
      ).has("open-payment");

    if (isDirectPaymentEntry) {
      section.classList.add(
        "is-payment-entry-pending"
      );
    }

    document.documentElement.classList.remove(
      "peguu-summary-payment-entry"
    );

    const addAndContinueButton =
      section.querySelector(
        "[data-peguu-add-and-continue]"
      );

    const finishPurchaseButton =
      section.querySelector(
        "[data-peguu-add-and-finish]"
      );

    const cancelEditButton =
      section.querySelector(
        "[data-peguu-cancel-edit]"
      );

    const editState =
      parseSessionJson(
        CART_EDIT_STORAGE_KEY
      );
    const isEditing =
      editState?.mode === "edit" &&
      Boolean(editState?.itemId);

    if (
      !addAndContinueButton ||
      !finishPurchaseButton
    ) {
      return;
    }

    const actionButtons = [
      addAndContinueButton,
      finishPurchaseButton,
    ];

    if (isEditing) {
      addAndContinueButton.textContent =
        "GUARDAR Y SEGUIR";
      finishPurchaseButton.textContent =
        "GUARDAR Y FINALIZAR COMPRA";

      if (cancelEditButton) {
        cancelEditButton.hidden = false;
      }
    }

    let isAdding = false;

    let addedResult = null;

    const paymentMount = section.querySelector(
      "[data-peguu-payment-mount]"
    );

    const actionsContainer = section.querySelector(
      ".peguu-sticker-summary__actions"
    );

    let paymentExpanded = false;
    let paymentLoading = false;
    let paymentSettleTimer = null;
    let paymentRequestId = 0;
    let latestCartSignature = "";
    let latestCart = null;
    let directPaymentCartReady =
      !isDirectPaymentEntry;
    let directPaymentAuthReady =
      !isDirectPaymentEntry;
    let directPaymentObserver = null;
    let directPaymentFallbackTimer = null;
    let directPaymentRevealTimer = null;
    const directPaymentStartedAt =
      window.performance.now();

    const canRestorePaymentFromReload =
      pageNavigationType === "reload";

    const positionExpandedPayment = () => {
      const payment =
        paymentMount?.querySelector(
          "[data-peguu-sticker-payment]"
        );

      if (
        !payment ||
        payment.dataset
          .deferInitialPosition !==
          "true"
      ) {
        return;
      }

      payment.removeAttribute(
        "data-defer-initial-position"
      );

      payment
        ?.peguuStickerPaymentPosition
        ?.();
    };

    const clearDirectPaymentPending = () => {
      window.clearTimeout(
        directPaymentFallbackTimer
      );
      directPaymentFallbackTimer = null;
      window.clearTimeout(
        directPaymentRevealTimer
      );
      directPaymentRevealTimer = null;
      directPaymentObserver?.disconnect();
      directPaymentObserver = null;
      section.classList.remove(
        "is-payment-entry-pending"
      );
      document.documentElement.classList.remove(
        "peguu-summary-payment-entry"
      );
    };

    const revealDirectPaymentEntry = () => {
      if (
        !isDirectPaymentEntry ||
        !directPaymentCartReady ||
        !directPaymentAuthReady
      ) {
        return;
      }

      const minimumLoadingTime = 500;
      const elapsedTime =
        window.performance.now() -
        directPaymentStartedAt;
      const remainingTime =
        Math.max(
          0,
          minimumLoadingTime - elapsedTime
        );

      if (remainingTime > 0) {
        if (!directPaymentRevealTimer) {
          directPaymentRevealTimer =
            window.setTimeout(
              revealDirectPaymentEntry,
              remainingTime
            );
        }

        return;
      }

      clearDirectPaymentPending();
      window.requestAnimationFrame(
        positionExpandedPayment
      );
    };

    const expandPayment = async (cart = null) => {
      if (paymentExpanded || paymentLoading || !paymentMount) {
        return;
      }

      const cartSignature =
        createCartSignature(cart);
      const storedPaymentState =
        parseSessionJson(
          PAYMENT_STORAGE_KEY
        ) || {};
      const matchesStoredCart =
        Boolean(cartSignature) &&
        storedPaymentState.cartSignature ===
          cartSignature;

      writeSessionJson(
        PAYMENT_STORAGE_KEY,
        matchesStoredCart
          ? {
              ...storedPaymentState,
              isExpanded: true,
              requiresSummary: false
            }
          : {
              isExpanded: true,
              step: "access",
              mode: "access",
              cartSignature,
              requiresSummary: false
            }
      );

      paymentExpanded = true;
      paymentLoading = true;
      const requestId =
        ++paymentRequestId;
      section.classList.add("is-payment-expanded");
      section.classList.remove("is-payment-settled");
      window.clearTimeout(paymentSettleTimer);
      paymentSettleTimer = window.setTimeout(() => {
        section.classList.add("is-payment-settled");
      }, 440);
      actionsContainer?.setAttribute("aria-hidden", "true");
      actionButtons.forEach((button) => {
        button.disabled = true;
      });
      paymentMount.hidden = false;
      paymentMount.setAttribute("aria-busy", "true");

      try {
        const payment = paymentMount.querySelector(
          "[data-peguu-sticker-payment]"
        );

        if (!payment) {
          throw new Error("No encontramos el bloque de pago.");
        }

        payment.classList.add("peguu-sticker-payment--embedded");

        if (!cartSignature) {
          payment.setAttribute(
            "data-defer-initial-position",
            "true"
          );
        } else {
          payment.removeAttribute(
            "data-defer-initial-position"
          );
        }

        const paymentApi =
          await waitForStickerPayment();

        if (
          requestId !== paymentRequestId ||
          paymentMount.hidden
        ) {
          return;
        }

        const wasInitialized =
          payment.dataset.paymentInitialized === "true";

        if (wasInitialized) {
          await paymentApi.refresh?.(payment);
        } else {
          paymentApi.init(payment);
        }

        if (requestId !== paymentRequestId) {
          return;
        }

        const initializedPaymentState =
          parseSessionJson(
            PAYMENT_STORAGE_KEY
          ) || {};

        const initializedCartSignature =
          cartSignature ||
          initializedPaymentState.cartSignature ||
          "";

        writeSessionJson(
          PAYMENT_STORAGE_KEY,
          {
            ...initializedPaymentState,
            isExpanded: true,
            step:
              initializedPaymentState.step === "checkout" ||
              initializedPaymentState.mode === "guest"
                ? "checkout"
                : "access",
            cartSignature:
              initializedCartSignature,
            requiresSummary: false
          }
        );
      } catch (error) {
        paymentExpanded = false;
        clearDirectPaymentPending();
        window.clearTimeout(paymentSettleTimer);
        section.classList.remove("is-payment-expanded");
        section.classList.remove("is-payment-settled");
        actionsContainer?.removeAttribute("aria-hidden");
        actionButtons.forEach((button) => {
          button.disabled = false;
        });
        paymentMount.hidden = true;
        showMessage(
          section,
          error.message || "No fue posible mostrar el pago. Inténtalo nuevamente."
        );
      } finally {
        if (requestId === paymentRequestId) {
          paymentLoading = false;
          paymentMount.removeAttribute("aria-busy");
        }
      }
    };

    const removeOpenPaymentParameter = () => {
      const currentUrl = new URL(
        window.location.href
      );

      if (
        !currentUrl.searchParams.has(
          "open-payment"
        )
      ) {
        return;
      }

      currentUrl.searchParams.delete(
        "open-payment"
      );

      window.history.replaceState(
        window.history.state,
        "",
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );
    };

    const showSummaryState = (
      cartSignature = "",
      { requiresSummary = false } = {}
    ) => {
      clearDirectPaymentPending();
      paymentRequestId += 1;
      paymentExpanded = false;
      paymentLoading = false;
      window.clearTimeout(paymentSettleTimer);
      section.classList.remove(
        "is-payment-expanded",
        "is-payment-settled"
      );
      actionsContainer?.removeAttribute(
        "aria-hidden"
      );
      actionButtons.forEach((button) => {
        button.disabled = false;
      });

      if (paymentMount) {
        paymentMount.hidden = true;
        paymentMount.removeAttribute("aria-busy");
      }

      writeSessionJson(
        PAYMENT_STORAGE_KEY,
        {
          isExpanded: false,
          step: "summary",
          mode: null,
          cartSignature,
          requiresSummary
        }
      );
    };

    const setActionsLoading = (
      activeButton,
      loading
    ) => {
      actionButtons.forEach((button) => {
        if (button === activeButton) {
          setButtonLoading(
            button,
            loading
          );
          return;
        }

        button.disabled = loading;
        button.setAttribute(
          "aria-disabled",
          loading ? "true" : "false"
        );
      });
    };

    const addOrderAndNavigate = async (
      activeButton,
      destinationUrl
    ) => {
        if (
          isAdding ||
          activeButton.disabled
        ) {
          return;
        }

        removeExistingMessage(
          section
        );

        const material =
          parseSessionJson(
            MATERIAL_STORAGE_KEY
          );

        const order =
          parseSessionJson(
            ORDER_STORAGE_KEY
          );

        const design =
          parseSessionJson(
            DESIGN_STORAGE_KEY
          );

        const isPaymentDestination =
          destinationUrl ===
          (section.dataset.paymentUrl ||
            "/pages/arma-tus-stickers-resumen");

        const latestItems =
          Array.isArray(
            latestCart?.items
          )
            ? latestCart.items
            : [];

        /*
         * En un resumen construido desde un carrito existente no hay un
         * pedido pendiente que guardar. Con sesión activa, el carrito que ya
         * renderizó el resumen es suficiente para abrir contacto/factura.
         */
        if (
          isPaymentDestination &&
          getCustomerToken() &&
          latestItems.length &&
          (!material || !order)
        ) {
          const hasInvalidPrice =
            latestItems.some(
              (item) =>
                item?.priceValid === false
            );

          if (hasInvalidPrice) {
            showMessage(
              section,
              "No es posible continuar al pago porque uno o más productos requieren actualizar su precio."
            );

            return;
          }

          await expandPayment(
            latestCart
          );

          return;
        }

        /*
         * Al volver de Shopify, el producto en edición ya puede haberse
         * limpiado aunque el carrito siga completo. Para pagar usamos ese
         * carrito existente y evitamos exigir otra selección de material.
         */
        if (
          isPaymentDestination &&
          (!material || !order)
        ) {
          isAdding = true;

          setActionsLoading(
            activeButton,
            true
          );

          try {
            const currentCart =
              await refreshCart();
            const currentItems =
              Array.isArray(
                currentCart?.items
              )
                ? currentCart.items
                : [];

            if (currentItems.length) {
              const hasInvalidPrice =
                currentItems.some(
                  (item) =>
                    item.priceValid === false
                );

              if (hasInvalidPrice) {
                throw new Error(
                  "No es posible continuar al pago porque uno o más productos requieren actualizar su precio."
                );
              }

              isAdding = false;

              setActionsLoading(
                activeButton,
                false
              );

              await expandPayment(
                currentCart
              );

              return;
            }
          } catch (error) {
            showMessage(
              section,
              error.message ||
                "No fue posible consultar tu carrito."
            );

            isAdding = false;

            setActionsLoading(
              activeButton,
              false
            );

            return;
          }

          isAdding = false;

          setActionsLoading(
            activeButton,
            false
          );
        }

        if (!material) {
          showMessage(
            section,
            "No encontramos el material seleccionado. Regresa a Materiales y vuelve a elegirlo."
          );

          return;
        }

        if (!order) {
          showMessage(
            section,
            "No encontramos la configuración del pedido. Regresa al paso de Medidas."
          );

          return;
        }

        const cartItem =
          buildCartItem({
            material,
            order,
            design,
          });

        if (isEditing) {
          const designWasRemoved =
            design?.uploadLater === true &&
            !design?.primaryFile;

          if (
            !cartItem.fileId &&
            !designWasRemoved
          ) {
            cartItem.fileId =
              editState.originalFileId ||
              null;
          }

          cartItem.fileId2 =
            editState.originalFileId2 ||
            null;
        }

        const cartItemFingerprint =
          createCartItemFingerprint(
            cartItem
          );

        try {
          validateCartItem(
            cartItem
          );
        } catch (error) {
          showMessage(
            section,
            error.message ||
              "Los datos del pedido están incompletos."
          );

          return;
        }

        const storedCartAddition =
          getStoredCartAddition(
            order,
            cartItemFingerprint
          );

        /*
         * El pedido ya fue agregado y el resumen acaba de entregarnos el
         * carrito vigente. Para clientes autenticados, FINALIZAR COMPRA sólo
         * abre el bloque de contacto; no vuelve a guardar ni a refrescar.
         */
        if (
          isPaymentDestination &&
          !isEditing &&
          getCustomerToken() &&
          storedCartAddition &&
          latestCart
        ) {
          addedResult =
            storedCartAddition;

          await expandPayment(
            latestCart
          );

          return;
        }

        isAdding = true;

        setActionsLoading(
          activeButton,
          true
        );

        try {
          let refreshedCart;

          if (isEditing) {
            if (
              typeof window.PeguuCart
                ?.updateItem !==
              "function"
            ) {
              throw new Error(
                "No fue posible guardar los cambios del producto."
              );
            }

            const itemWithDesign =
              await ensureCartItemDesignFile(
                cartItem
              );

            refreshedCart =
              await window.PeguuCart.updateItem(
                editState,
                itemWithDesign
              );

            window.sessionStorage.removeItem(
              CART_EDIT_STORAGE_KEY
            );
            clearCurrentOrder();
          } else {
            const result =
              addedResult ||
              getStoredCartAddition(
                order,
                cartItemFingerprint
              ) ||
              await addCurrentOrderToCart(
                cartItem
              );

            addedResult = result;

            saveCartAddition(
              result,
              cartItemFingerprint
            );

            refreshedCart =
              await refreshCart();
          }

          const hasInvalidPrice =
            refreshedCart?.items?.some(
              (item) =>
                item.priceValid === false
            ) === true;

          if (
            isPaymentDestination &&
            hasInvalidPrice
          ) {
            throw new Error(
              "No es posible continuar al pago porque uno o más productos requieren actualizar su precio."
            );
          }

          if (!isEditing) {
            clearCurrentOrder();

            notifyCartUpdated({
              source:
                addedResult?.source,

              item:
                addedResult?.item,

              action:
                "add",

              cart:
                refreshedCart,
            });
          }

          if (!isPaymentDestination) {
            /*
             * AGREGAR OTRO inicia un ciclo nuevo. Aunque haya quedado una
             * URL antigua con open-payment, el siguiente regreso debe
             * aterrizar primero en el resumen del carrito actualizado.
             */
            showSummaryState(
              createCartSignature(
                refreshedCart
              ),
              {
                requiresSummary: true
              }
            );
            removeOpenPaymentParameter();
          }

          /*
           * El carrito refrescado ya incluye el producto que se acaba de
           * configurar. Decidimos el checkout con esa fuente real para no
           * depender del badge ni del estado previo del drawer.
           */
          const refreshedItems =
            Array.isArray(
              refreshedCart?.items
            )
              ? refreshedCart.items
              : [];

          const isSummaryPayment =
            isPaymentDestination &&
            destinationUrl ===
              (section.dataset.paymentUrl ||
                "/pages/arma-tus-stickers-resumen");

          if (isSummaryPayment) {
            await expandPayment(
              refreshedCart
            );
          } else {
            const resolvedDestinationUrl =
              isPaymentDestination &&
              refreshedItems.length > 1
                ? section.dataset.multiPaymentUrl ||
                  "/pages/arma-tus-stickers-resumen"
                : destinationUrl;

            window.location.assign(resolvedDestinationUrl);
          }
        } catch (error) {
          console.error(
            "Peguu Summary: no fue posible agregar el pedido."
          );

          let errorMessage =
            error.message ||
            "No fue posible agregar el pedido al carrito.";

          /*
           * Si el token expiró o no es válido,
           * conservamos el pedido actual para
           * que el usuario pueda volver a intentarlo.
           */

          if (
            error?.status === 401
          ) {
            errorMessage =
              "Tu sesión expiró. Inicia sesión nuevamente para agregar el pedido.";
          }

          if (
            error?.status === 404
          ) {
            errorMessage =
              "El material seleccionado ya no está disponible.";
          }

          showMessage(
            section,
            errorMessage
          );

          isAdding = false;

          setActionsLoading(
            activeButton,
            false
          );
        }
    };

    cancelEditButton?.addEventListener(
      "click",
      () => {
        if (!isEditing || isAdding) {
          return;
        }

        window.sessionStorage.removeItem(
          CART_EDIT_STORAGE_KEY
        );
        clearCurrentOrder();
        window.location.assign(
          editState.returnUrl ||
          "/pages/arma-tus-stickers-resumen"
        );
      }
    );

    addAndContinueButton.addEventListener(
      "click",
      () => addOrderAndNavigate(
        addAndContinueButton,
        section.dataset.materialsUrl ||
          "/pages/materiales"
      )
    );

    finishPurchaseButton.addEventListener(
      "click",
      () => addOrderAndNavigate(
        finishPurchaseButton,
        section.dataset.paymentUrl ||
          "/pages/arma-tus-stickers-resumen"
      )
    );

    let hasRestoredPaymentState = false;

    const restorePaymentState = (cart) => {
      latestCart = cart || null;

      const cartSignature =
        createCartSignature(cart);
      latestCartSignature =
        cartSignature;

      if (isDirectPaymentEntry) {
        directPaymentCartReady =
          Boolean(cartSignature);
        revealDirectPaymentEntry();
      }

      const storedPaymentState =
        parseSessionJson(
          PAYMENT_STORAGE_KEY
        ) || {};

      /*
       * open-payment representa una intención nueva sólo al navegar hacia
       * esta página. En reload se exige además el mismo carrito y estado.
       */
      const forceOpen =
        !hasRestoredPaymentState &&
        pageNavigationType ===
          "navigate" &&
        new URLSearchParams(
          window.location.search
        ).has("open-payment");
      const requiresSummary =
        storedPaymentState.requiresSummary ===
          true;
      const matchesStoredCart =
        Boolean(cartSignature) &&
        storedPaymentState.cartSignature ===
          cartSignature;

      /*
       * Al iniciar sesión, el carrito cambia de invitado a cliente mientras
       * el pago ya está abierto. Ese cambio no debe devolver al resumen ni
       * ocultar el formulario de checkout.
       */
      if (
        paymentExpanded ||
        paymentLoading
      ) {
        writeSessionJson(
          PAYMENT_STORAGE_KEY,
          {
            ...storedPaymentState,
            isExpanded: true,
            cartSignature,
            requiresSummary: false
          }
        );
        hasRestoredPaymentState = true;

        /*
         * El resumen puede terminar de renderizar después de abrir el pago.
         * Al cambiar su altura vuelve a anclar el saludo debajo del header,
         * sin reiniciar ni volver a procesar el carrito.
         */
        window.requestAnimationFrame(
          positionExpandedPayment
        );
        return;
      }

      if (!cartSignature) {
        showSummaryState();
        window.sessionStorage.removeItem(
          PAYMENT_STORAGE_KEY
        );
        hasRestoredPaymentState = true;
        return;
      }

      if (
        requiresSummary
      ) {
        showSummaryState(
          cartSignature
        );
        removeOpenPaymentParameter();
        hasRestoredPaymentState = true;
        return;
      }

      if (forceOpen) {
        hasRestoredPaymentState = true;
        expandPayment(cart);
        return;
      }

      if (
        !hasRestoredPaymentState &&
        canRestorePaymentFromReload &&
        matchesStoredCart &&
        storedPaymentState.isExpanded === true
      ) {
        hasRestoredPaymentState = true;
        expandPayment(cart);
        return;
      }

      if (!hasRestoredPaymentState) {
        showSummaryState(
          cartSignature
        );
        removeOpenPaymentParameter();
        hasRestoredPaymentState = true;
        return;
      }

      if (!matchesStoredCart) {
        showSummaryState(
          cartSignature
        );
        removeOpenPaymentParameter();
      } else if (!storedPaymentState.step) {
        writeSessionJson(
          PAYMENT_STORAGE_KEY,
          {
            ...storedPaymentState,
            isExpanded: false,
            step: "summary",
            mode: null,
            cartSignature
          }
        );
      }

      hasRestoredPaymentState = true;
    };

    section.addEventListener(
      "peguu:cart-summary:updated",
      (event) => {
        restorePaymentState(
          event.detail?.cart
        );
      }
    );

    /*
     * Desde el carrito preparamos acceso o checkout al mismo tiempo que el
     * resumen. La vista se revela cuando ambos terminaron de inicializarse.
     */
    if (isDirectPaymentEntry) {
      const payment =
        paymentMount?.querySelector(
          "[data-peguu-sticker-payment]"
        );

      const syncDirectPaymentAuthState = () => {
        directPaymentAuthReady =
          payment?.dataset
            .paymentAuthPending ===
          "false";
        revealDirectPaymentEntry();
      };

      directPaymentFallbackTimer =
        window.setTimeout(
          clearDirectPaymentPending,
          7000
        );

      if (payment) {
        directPaymentObserver =
          new MutationObserver(
            syncDirectPaymentAuthState
          );
        directPaymentObserver.observe(
          payment,
          {
            attributes: true,
            attributeFilter: [
              "data-payment-auth-pending"
            ]
          }
        );
        syncDirectPaymentAuthState();
      }

      hasRestoredPaymentState = true;
      expandPayment();
    }

    window.addEventListener(
      "pageshow",
      (event) => {
        if (!event.persisted) {
          return;
        }

        showSummaryState(
          latestCartSignature
        );
        removeOpenPaymentParameter();
        hasRestoredPaymentState = true;
      }
    );
  };


  const syncAllSummaryGreetings = () => {
    document
      .querySelectorAll(
        "[data-peguu-sticker-summary]"
      )
      .forEach(syncSummaryGreeting);
  };


  [
    "peguu:customer-authenticated",
    "peguu:customer-updated",
    "peguu:customer-logged-out",
    "peguu:auth-state-changed",
  ].forEach((eventName) => {
    window.addEventListener(
      eventName,
      syncAllSummaryGreetings
    );
  });


  const initializeAllSummaries = (
    scope = document
  ) => {
    const sections =
      scope.matches?.(
        "[data-peguu-sticker-summary]"
      )
        ? [scope]
        : Array.from(
            scope.querySelectorAll(
              "[data-peguu-sticker-summary]"
            )
          );

    sections.forEach(
      initializeSummary
    );
  };


  /* =========================
     DOM READY
  ========================= */

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeAllSummaries();
      }
    );
  } else {
    initializeAllSummaries();
  }


  /* =========================
     SHOPIFY THEME EDITOR
  ========================= */

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAllSummaries(
        event.target
      );
    }
  );
})();
